import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Bug,
  Lightbulb,
  Heart,
  MessageCircle,
  Send,
  Check,
  AlertTriangle,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";

const CATEGORIES = [
  {
    key: "bug",
    label: "Bug",
    icon: Bug,
    hint: "Something broke or behaves wrong",
    color: "text-red-300",
    border: "border-red-500/30",
    bg: "bg-red-500/[0.06]",
  },
  {
    key: "suggestion",
    label: "Suggestion",
    icon: Lightbulb,
    hint: "An idea or improvement",
    color: "text-[#D4AF37]",
    border: "border-[#D4AF37]/30",
    bg: "bg-[#D4AF37]/[0.06]",
  },
  {
    key: "praise",
    label: "Praise",
    icon: Heart,
    hint: "Tell us what you love",
    color: "text-emerald-300",
    border: "border-emerald-500/30",
    bg: "bg-emerald-500/[0.06]",
  },
  {
    key: "other",
    label: "Other",
    icon: MessageCircle,
    hint: "Anything else",
    color: "text-white/90",
    border: "border-white/20",
    bg: "bg-white/[0.04]",
  },
];

const SEVERITIES = [
  { key: "low", label: "Low", hint: "Cosmetic, can wait" },
  { key: "medium", label: "Medium", hint: "Annoying but workable" },
  { key: "high", label: "High", hint: "Blocks me from using a feature" },
];

export default function FeedbackPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [category, setCategory] = useState("bug");
  const [severity, setSeverity] = useState("medium");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;
    setSubmitting(true);
    try {
      // Best-effort: where the user came from before /feedback
      const referrerPath =
        document.referrer && document.referrer.startsWith(window.location.origin)
          ? document.referrer.replace(window.location.origin, "")
          : location.pathname;

      await api.post("/feedback", {
        category,
        severity: category === "bug" ? severity : null,
        title: title.trim(),
        description: description.trim(),
        page_url: referrerPath,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto py-20" data-testid="feedback-success">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-6">
            <Check className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.8} />
          </div>
          <h1 className="font-serif text-3xl text-white mb-3">Thank you</h1>
          <p className="text-white/65 text-sm leading-relaxed font-sans max-w-md mx-auto mb-8">
            Your feedback is in front of the team. Every beta report directly shapes what we ship next — we read all of them personally.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                setDone(false);
                setTitle("");
                setDescription("");
              }}
              data-testid="feedback-another"
              className="text-[#D4AF37] hover:text-[#B5952F] text-xs tracking-[0.2em] uppercase font-sans transition-colors px-4 py-2 border border-[#D4AF37]/30 hover:border-[#D4AF37]/60"
            >
              Send another
            </button>
            <button
              type="button"
              onClick={() => navigate("/portfolio")}
              data-testid="feedback-back-to-dashboard"
              className="text-white/60 hover:text-white text-xs tracking-[0.2em] uppercase font-sans transition-colors px-4 py-2"
            >
              Back to portfolio
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="feedback-page">
      {/* Beta banner */}
      <div className="flex items-center gap-2 mb-4">
        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] font-sans">
          Private Beta · Direct Line
        </p>
      </div>
      <h1 className="font-serif text-3xl sm:text-4xl text-white tracking-tight mb-3">
        Send us anything.
      </h1>
      <p className="text-white/60 text-sm leading-relaxed font-sans max-w-xl mb-10">
        Bugs, ideas, half-formed thoughts — they all matter during the beta. We read every submission personally and prioritise what to ship next based on your input.
      </p>

      <form onSubmit={submit} className="space-y-8">
        {/* ── Category ─────────────────────────────────────────── */}
        <Section label="Type">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const active = category === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCategory(c.key)}
                  data-testid={`feedback-category-${c.key}`}
                  className={`flex flex-col items-start gap-2 p-4 border text-left transition-all ${
                    active
                      ? `${c.border} ${c.bg}`
                      : "border-white/[0.10] bg-white/[0.01] hover:border-white/15"
                  }`}
                >
                  <Icon
                    className={`w-4 h-4 ${active ? c.color : "text-white/60"}`}
                    strokeWidth={1.6}
                  />
                  <div>
                    <p className={`text-sm font-medium ${active ? "text-white" : "text-white/90"}`}>
                      {c.label}
                    </p>
                    <p className="text-[10px] text-white/55 mt-0.5 font-sans">{c.hint}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </Section>

        {/* ── Severity (bugs only) ─────────────────────────────── */}
        {category === "bug" && (
          <Section label="How blocking is it?">
            <div className="grid grid-cols-3 gap-2">
              {SEVERITIES.map((s) => {
                const active = severity === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSeverity(s.key)}
                    data-testid={`feedback-severity-${s.key}`}
                    className={`p-3 border text-left transition-all ${
                      active
                        ? "border-[#D4AF37]/40 bg-[#D4AF37]/[0.06]"
                        : "border-white/[0.10] bg-white/[0.01] hover:border-white/15"
                    }`}
                  >
                    <p className={`text-xs font-medium ${active ? "text-[#D4AF37]" : "text-white/90"}`}>
                      {s.label}
                    </p>
                    <p className="text-[10px] text-white/60 mt-1 font-sans">{s.hint}</p>
                  </button>
                );
              })}
            </div>
          </Section>
        )}

        {/* ── Title ────────────────────────────────────────────── */}
        <Section label="Summary" required>
          <input
            required
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="feedback-title"
            placeholder={
              category === "bug"
                ? "e.g. FX rates show 0 on dashboard after refresh"
                : category === "suggestion"
                  ? "e.g. Add a Cyprus capital-gains calculator"
                  : "One line — what's this about?"
            }
            maxLength={140}
            className="w-full bg-[#070707] border border-white/[0.12] text-white px-4 py-3 text-sm font-sans placeholder:text-white/60 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
          />
        </Section>

        {/* ── Description ──────────────────────────────────────── */}
        <Section
          label="Details"
          required
          hint={
            category === "bug"
              ? "What were you doing? What did you expect? What happened instead?"
              : "Any context that would help us understand."
          }
        >
          <textarea
            required
            rows={6}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="feedback-description"
            placeholder={
              category === "bug"
                ? "Steps to reproduce, screenshots URLs, what should have happened…"
                : "Tell us more — the more context, the better."
            }
            maxLength={4000}
            className="w-full bg-[#070707] border border-white/[0.12] text-white px-4 py-3 text-sm font-sans placeholder:text-white/60 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all resize-none"
          />
          <p className="text-right text-[10px] text-white/60 font-mono mt-1">
            {description.length}/4000
          </p>
        </Section>

        {/* ── Helpful note ─────────────────────────────────────── */}
        {category === "bug" && (
          <div className="flex items-start gap-2.5 bg-amber-500/[0.05] border border-amber-500/15 px-4 py-3" data-testid="feedback-context-note">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-300/80 mt-0.5 flex-shrink-0" />
            <p className="text-white/80 text-xs leading-relaxed font-sans">
              We auto-attach the page you came from and your browser type — no need to mention them. Focus your message on what went wrong.
            </p>
          </div>
        )}

        {/* ── Submit ───────────────────────────────────────────── */}
        <div className="flex items-center justify-between pt-2">
          <p className="text-white/65 text-xs font-sans">
            Replies go to <span className="text-white/85">{/* email shown by app via toast */}your account email.</span>
          </p>
          <button
            type="submit"
            disabled={submitting || !title.trim() || !description.trim()}
            data-testid="feedback-submit"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-7 py-3 text-sm font-medium tracking-wide hover:bg-[#B5952F] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send className="w-3.5 h-3.5" />
            {submitting ? "Sending…" : "Send feedback"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ label, required, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2.5">
        <label className="text-[10px] tracking-[0.2em] uppercase text-white/80 font-sans">
          {label}
          {required && <span className="text-[#D4AF37] ml-1">*</span>}
        </label>
        {hint && (
          <span className="text-[10px] text-white/65 font-sans hidden sm:inline-block max-w-xs text-right">
            {hint}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}
